import React, { useEffect } from "react";
import { Divider, TextField } from "@mui/material";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

import DrawerWithBookmarkButton from "modules/common/components/drawers/drawerWithBookmarkButton"
import SmsEditor from "./smsEditor";
import DateAndTime from "modules/common/components/forms/inputs/dateAndTime";
import HoverText from "modules/documents/components/action-buttons/hoverText";
import SendTestSmsInput from "./sendTestSmsInput";
import { ISMS } from "./models";
import { IdNamePair } from "modules/common/models";
import { smsApi } from "api/instances";
import { DEFAULT_SMS, smsSlice } from "./reducer";
import shallowEquals from "utils/shallowEquals";
import ToAudienceSelection from "./toAudienceSelection";
import AudienceFooter from "modules/settings/components/audienceFooter";

import "./styles/smsSettingsPanel.sass";

const MAX_TITLE_LENGTH = 100;

interface ISmsSettingsPanel {
    open: boolean;
    toggleOpen: () => void;
    updateSMS: (updatedSMSFields: Partial<ISMS>) => void;
    updatePreview: (text: string) => void;
    onSaveSMS: () => void;
    sms: ISMS;
    audienceUsers: number;
    totalAudienceUsers: number;
    setAudienceUsers: (users: number) => void;
    setTotalUsers: (users: number) => void;
    onSuccessMessage: (message: string) => void;
    onErrorMessage: (message: string) => void;
    disableEditor: boolean;
    editorRef: any | null;
};

const SmsSettingsPanel: React.FC<ISmsSettingsPanel> = ({
    open,
    toggleOpen,
    updateSMS,
    updatePreview,
    onSaveSMS,
    audienceUsers,
    totalAudienceUsers,
    setAudienceUsers,
    setTotalUsers,
    onSuccessMessage,
    onErrorMessage,
    disableEditor,
    editorRef
}) => {
    const { 
        sms,
        testNumbers,
        changedSinceLastSaved,
        shouldSendTestSMS
    } = useSelector((state: GlobalApplicationState) => state.sms.editor);

    const audiences = useSelector((state: GlobalApplicationState) => state.audiences.audiences);
    const [title, setTitle] = React.useState(sms.title);

    const [isInitialLoad, setIsInitialLoad] = React.useState(true);
    const [isSendingTestSMS, setIsSendingTestSMS] = React.useState(false);
    const [fetchingAudienceUsers, setFetchingAudienceUsers] = React.useState(false);
    const titleAtMaxLength = title.length === MAX_TITLE_LENGTH;
    const dispatch = useDispatch();

    const setNumbersForTest = (testNumbers: string[]) => {
        dispatch({ type: smsSlice.actions.SET_TEST_SMS, payload: { testNumbers } });
    }

    const setShouldSendTestSMS = (shouldSend: boolean) => {
        dispatch({ type: smsSlice.actions.SET_SHOULD_SEND_TEST_SMS, payload: { shouldSend } });
    }

    const onClickSendTestSMS = async () => {
        if (!sms.id || changedSinceLastSaved) {
            onSaveSMS();
            setShouldSendTestSMS(true);
        }
        else {
            setShouldSendTestSMS(true);
        }
    }

    const onUpdateTitle = (title: string) => {
        if(title.length > 100) {
            return;
        }

        setTitle(title);
    }

    const onClearAudiences = () => {
        updateSMS({ audiences: [] });
    }

    const onSetSelectAll = (selectAll: boolean) => {
        updateSMS({ sendToAllUsers: selectAll });

        if(selectAll) {
            onClearAudiences();
        }
    }

    const audienceFooter = <AudienceFooter 
        selectAllChecked={sms.sendToAllUsers} 
        setSelectAll={onSetSelectAll} 
        clearAll={() => onClearAudiences()}
        buttonClassName={"sms-audience-footer-clear-all-button"}
        containerClassName={"sms-audience-footer"}
        checkBoxId={"send-to-all-employees-checkbox-sms-settings-panel"}
    />

    const onChangeAudience = (audiences: IdNamePair<string, string>[], updateTotal?: boolean) => {
        if (!sms.sendToAllUsers) {
            updateSMS({ audiences });
        }

        if(updateTotal) {
            updateAudienceTotals(audiences.map(a => a.id));
        }
    }

    const updateAudienceTotals = async(audiences?: string[]) => {
        const smsAudiences = audiences ?? sms.audiences.map(a => a.id);

        if(smsAudiences.length === 0 && !sms.sendToAllUsers) {
            setAudienceUsers(0);
            setTotalUsers(0);
            return;
        }

        setFetchingAudienceUsers(true);

        const totals = (await smsApi.getAudienceTotals(smsAudiences, sms.sendToAllUsers)).data;

        setAudienceUsers(totals.audienceOptedUsers);
        setTotalUsers(totals.audienceTotalUsers);
        setFetchingAudienceUsers(false);
    }

    useEffect(() => {
        //If we have any audiences selected OR sendToAllUsers and we're loading for the first time
        const shouldUpdateTotals = (!shallowEquals(DEFAULT_SMS.audiences, sms.audiences) || sms.sendToAllUsers) && isInitialLoad;

        if(shouldUpdateTotals) {
            updateAudienceTotals();
        }

        setIsInitialLoad(false);
    }, [isInitialLoad, sms]);

    useEffect(() => {
        const sendTestSMS = async () => {
            setIsSendingTestSMS(true);

            try {
                await smsApi.sendTestSMS(sms.id, testNumbers);
                onSuccessMessage("Successfully sent your test SMS message.");
            }
            catch {
                onErrorMessage("There was an error. Please try again.");
            }
            finally {
                setIsSendingTestSMS(false);
                toggleOpen();
            }
        }

        const handleSendTestSMS = async () => {
            if(shouldSendTestSMS && sms.id && !changedSinceLastSaved) {
                setShouldSendTestSMS(false);
                await sendTestSMS();
            }
        };

        handleSendTestSMS();
    }, [shouldSendTestSMS, sms.id, changedSinceLastSaved])

    const InternalTitleField = (
        <>
            <div className="title-container">
                <div className="internal-title">
                    Internal title
                </div>
                <TextField 
                    size="small" 
                    placeholder="Enter a title, name or label" 
                    value={title}
                    fullWidth
                    onBlur={(e) => updateSMS({title: e.target.value})}
                    onChange={(e) => onUpdateTitle(e.target.value)}
                    multiline
                />
            </div>

            <div className="letter-count" style={{color: titleAtMaxLength ? "#b72026" : "#7F7F7F"}}>
                {`${title.length} / ${MAX_TITLE_LENGTH}`}
            </div>
        </>
    );

    const FromNumberField = (
        <div className="from-number-container">
            <div className="from-section">
                From
            </div>

            <div className="from-content">
                {sms.fromNumber}
            </div>
        </div>
    );

    const SMSEditorField = (
        <div className="sms-editor-container-settings-panel">
            <div className="enter-message">
                Enter message
            </div>

            <SmsEditor 
                onChange={(plaintext, html) => updateSMS({plainBody: plaintext, htmlBody: html})}
                value={sms.htmlBody}
                maxTextLength={500}
                initialTextLength={sms.plainBody.replaceAll("\n", " ").length}
                onEditorUpdate={updatePreview}
                id={"sms-editor-settings-panel"}
                placeholder="Start typing here..."
                disabled={disableEditor}
                externalEditorRef={editorRef}
                useExternalEditorRef
            /> 
        </div>
    );

    const DateAndTimeField = (
        <>
            <HoverText label="Send on" labelContainerClassName="send-on-label-container" style={{marginBottom: "6px"}}>
                Schedule when this SMS should be sent. If left blank, it will be sent immediately after you click 'Send’ in One Last Check.
            </HoverText>
            <DateAndTime
                clearable
                onChangeDate={dateString => updateSMS({ sentTime: dateString })}
                date={
                    sms.sentTime
                        ? moment(sms.sentTime).isBefore(moment()) 
                            ? moment().toISOString() 
                            : sms.sentTime 
                        : undefined
                }
                dateStyle={{ width: 250 }}
                showIcons
                minDate={moment()}
                timePlaceholder="Select time"
                initializeDateWithCurrentTime
            />
        </>
    );

    const TestNumbersField = (
        <div className="test-numbers-container">
            <SendTestSmsInput 
                testNumbers={testNumbers}
                setNumbersForTest={setNumbersForTest}
                isSendingTestSMS={isSendingTestSMS}
                onClickSendTestSMS={onClickSendTestSMS}
                sendTestSMSDisabled={!sms.plainBody || sms.plainBody === ""}
            />
        </div>
    );

    return ( 
        <DrawerWithBookmarkButton
            open={open}
            onBookmarkClick={() => null}
            PaperProps={{ id: "sms-settings-drawer" }}
        >
            <div className="input-fields-container">
                {InternalTitleField}

                {FromNumberField}
                
                <ToAudienceSelection 
                    isFetching={fetchingAudienceUsers}
                    currentUsers={audienceUsers}
                    totalAudienceUsers={totalAudienceUsers}
                    emptyChipText={sms.sendToAllUsers ? "All users" : "No recipients selected"}
                    disableOptions={sms.sendToAllUsers}
                    audienceFooter={audienceFooter}
                    defaultOptions={audiences.map(a => ({ id: a.id, name: a.displayName }))}
                    selectedOptions={sms.audiences}
                    onClose={updateAudienceTotals}
                    onChange={(audiences) => onChangeAudience(audiences)}
                    onChipRemove={(audiences) => onChangeAudience(audiences, true)}
                    sendToAllUsers={sms.sendToAllUsers}
                    audienceIds={sms.audiences.map(a => a.id)}
                    filterByOptedIn
                    fileName="send-sms-to.csv"
                />

                {SMSEditorField}

                <Divider className="sms-settings-divider"/>

                {DateAndTimeField}

                <Divider className="sms-settings-divider"/>

                {TestNumbersField}
                
            </div>
        </DrawerWithBookmarkButton>
    );
}

export default SmsSettingsPanel;